// react
import { MouseEvent, useContext, useState } from 'react';

// types
import { Feedback, Form, FormField } from '../../../@types';

// i18n
import { Trans, useTranslation } from 'react-i18next';

// context
import { ContextBanner } from '../../../App.context';

// mui
import { Alert, AlertTitle, Box, Grid, Typography } from '@mui/material';

// dialogs
import { DialogActions } from '../DialogActions/DialogActions';

// utils
import { translateFormName } from '../../../utils/translations';

// icons
import { ArrowForwardOutlined as IconForward } from '@mui/icons-material';

// props
export interface DialogUpdateFormProps {
	fieldsAdded?: FormField[];
	fieldsRemoved?: FormField[];
	fieldsRenamed?: FormField[];
	form: Form;
	isOpen: boolean;
	onClose: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
	onSuccess?: () => void;
}

export const DialogUpdateForm = ({
	fieldsAdded = [],
	fieldsRemoved = [],
	fieldsRenamed = [],
	form,
	isOpen,
	onClose,
	onSuccess,
}: DialogUpdateFormProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { t } = useTranslation();

	// context
	const { setBanner } = useContext(ContextBanner);

	// state
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	// vars
	const formName = translateFormName(form.name, t);

	// handlers
	const handleSubmit = async (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear feedback
		setFeedback(null);

		// set loading
		setIsLoading(true);

		const fetchUpdateForm = await fetch(`${process.env.REACT_APP_API_URL}/forms/${form.id}`, {
			method: 'PATCH',
			body: JSON.stringify({
				fields: form.fields,
			}),
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		});
		const resUpdateForm = await fetchUpdateForm.json();

		// clear loading
		setIsLoading(false);

		if (resUpdateForm.error) {
			const feedbackNew = {
				alert: {
					children: t('error.default'),
				},
			};

			if (resUpdateForm.error.id === 'FORM_UNAUTHORIZED_PATCH') {
				feedbackNew.alert.children = t('error.unauthorized');
			}

			setFeedback(feedbackNew);
		} else {
			// set banner
			setBanner({
				hasClose: true,
				message: t('alert.formUpdated', { name: formName }),
				severity: 'success',
			});

			if (onSuccess) {
				onSuccess();
			}

			// close dialog
			if (onClose) {
				onClose();
			}
		}
	};

	const handleClose = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear feedback
		setFeedback(null);

		if (onClose) {
			onClose(e);
		}
	};

	const hasFieldsAdded = fieldsAdded.length > 0;
	const hasFieldsRemoved = fieldsRemoved.length > 0;
	const hasFieldsRenamed = fieldsRenamed.length > 0;
	const hasDiff = hasFieldsAdded || hasFieldsRemoved || hasFieldsRenamed;

	return (
		<DialogActions
			actions={[
				{
					children: t('cancel'),
					onClick: handleClose,
					variant: 'text',
				},
				{
					children: t('saveForm'),
					loading: isLoading,
					onClick: handleSubmit,
					variant: 'contained',
				},
			]}
			alignItems="center"
			IconCircleProps={{
				type: 'form',
			}}
			isOpen={isOpen}
			maxWidth={460}
			onClose={handleClose}
			subtitle={{
				children: formName,
			}}
			title={{
				children: t('saveForm'),
			}}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Trans
						components={{
							strong: <Typography color="brand.orange.600" component="span" fontWeight={600} />,
						}}
						i18nKey="dialogUpdateForm.content"
						values={{ name: formName }}
					/>
				</Grid>
				{hasDiff && (
					<Grid item xs={12}>
						<Box
							sx={(theme) => {
								return {
									backgroundColor: theme.palette.brand.grey[100],
									borderRadius: 2,
									padding: theme.spacing(2),
								};
							}}>
							<Grid container spacing={2}>
								{hasFieldsAdded && (
									<Grid item xs={12}>
										<Typography fontWeight={500}>{t('fieldsAdded')}</Typography>
										<Box component="ul" sx={{ margin: 0 }}>
											{fieldsAdded.map((field) => {
												return <Box component="li">{field.label}</Box>;
											})}
										</Box>
									</Grid>
								)}
								{hasFieldsRenamed && (
									<Grid item xs={12}>
										<Typography fontWeight={500}>{t('fieldsRenamed')}</Typography>
										<Box component="ul" sx={{ margin: 0 }}>
											{fieldsRenamed.map((field) => {
												const fieldNew = form.fields.find((f) => f.uuid === field.uuid);
												return (
													<Box component="li">
														<Grid alignItems="center" container>
															<Grid item>{field.label}</Grid>
															<Grid item>
																<IconForward sx={{ height: 16 }} />
															</Grid>
															<Grid item>{fieldNew.label}</Grid>
														</Grid>
													</Box>
												);
											})}
										</Box>
									</Grid>
								)}
								{hasFieldsRemoved && (
									<Grid item xs={12}>
										<Typography fontWeight={500}>{t('fieldsRemoved')}</Typography>
										<Box component="ul" sx={{ margin: 0 }}>
											{fieldsRemoved.map((field) => {
												return <Box component="li">{field.label}</Box>;
											})}
										</Box>
									</Grid>
								)}
							</Grid>
						</Box>
					</Grid>
				)}

				{hasFieldsRemoved && (
					<Grid item xs={12}>
						<Alert severity="warning">
							<AlertTitle children={t('dataMayBeLost')} />
							{t('alert.deleteFields')}
						</Alert>
					</Grid>
				)}

				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</Grid>
		</DialogActions>
	);
};
